<template>
  <v-container v-if="loadedSettings" fluid>
    <v-form>
      <v-card class="mb-5" outlined>
        <v-card-title>{{ $t('settings.orders') }}</v-card-title>
        <v-container fluid>
          <v-row>
            <v-col cols="12" lg="6" md="10" offset-xl="1" sm="10" xl="5">
              <v-row class="text-uppercase font-weight-bold">
                <v-col>{{ $t('settings.clientsDeadlines') }}</v-col>
              </v-row>
              <v-row class="align-center">
                <v-col cols="8" lg="9" md="8" sm="8" xl="10">
                  <v-card-text
                    class="text-uppercase pa-0"
                  >{{ $t('settings.ordersClientPickupDeadline') }}
                  </v-card-text>
                </v-col>
                <v-col cols="4" lg="3" md="4" sm="4" xl="2">
                  <validation-provider
                    v-slot="{ errors }"
                    name="max in store pickup client delay"
                    rules="min_value:1|integer"
                  >
                    <v-text-field
                      v-model="loadedSettings.globalClientDelay"
                      :disabled="loading"
                      :error-messages="errors"
                      :label="$t('settings.days')"
                      class="text-uppercase"
                      dense
                      hide-details="auto"
                      outlined
                      required
                      type="number"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-switch
                    v-model="loadedSettings.activatePickupTimeout"
                    :label="$t('settings.activatePickupTimeout')"
                    :messages="$t('settings.activatePickupTimeoutMessage')"
                  />
                  <v-row v-show="loadedSettings.activatePickupTimeout" class="pl-10">
                    <v-col cols="12">
                      <v-switch
                        v-model="loadedSettings.activatePickupDeadlineAdjustment"
                        :label="$t('settings.activatePickupDeadlineAdjustment')"
                        :messages="$t('settings.activatePickupDeadlineAdjustmentMessage')"
                        dense
                      />
                    </v-col>
                    <v-col v-if="loadedSettings.activatePickupDeadlineAdjustment" cols="6" lg="5">
                      <validation-provider v-slot="{ errors }" name="pickup timeout adjustment hour" rules="required">
                        <v-text-field
                          v-model="loadedSettings.pickupDeadlineAdjustmentHour"
                          :error-messages="errors"
                          :label="$t('settings.preciseHour')"
                          dense
                          type="time"
                          hide-details="auto"
                        >
                        </v-text-field>
                      </validation-provider>
                    </v-col>
                  </v-row>
                  <v-row v-show="loadedSettings.activatePickupTimeout" class="pl-10 mt-0">
                    <v-col cols="12">
                      <v-switch
                        v-model="loadedSettings.activatePickupDeadlineExtension"
                        :label="$t('settings.activatePickupDeadlineExtension')"
                        :messages="$t('settings.activatePickupDeadlineExtensionMessage')"
                        dense
                      />
                    </v-col>
                    <v-col v-if="loadedSettings.activatePickupDeadlineExtension" offset="1"  cols="7" lg="8" md="7" sm="7" xl="9">
                      <v-card-text
                        class="text-uppercase pa-0"
                      >{{ $t('settings.pickupDeadlineExtensionLimitMessage') }}
                      </v-card-text>
                    </v-col>
                    <v-col v-if="loadedSettings.activatePickupDeadlineExtension" cols="4" lg="3" md="4" sm="4" xl="2">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('settings.pickupDeadlineExtensionLimitMessage')"
                        rules="required|min_value:1"
                      >
                        <v-text-field
                          v-model="loadedSettings.pickupDeadlineExtensionLimit"
                          :error-messages="errors"
                          :label="$t('settings.days')"
                          dense
                          outlined
                          type="number"
                          hide-details="auto"
                        >
                        </v-text-field>
                      </validation-provider>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
      <v-card outlined>
        <v-card-title>{{ $t('order.subOrders') }}</v-card-title>
        <v-container fluid>
          <v-card class="mb-5" outlined>
            <v-card-subtitle>{{ $t('subOrder.clickAndCollect') }}</v-card-subtitle>
            <v-container fluid>
              <v-row>
                <v-col cols="12" lg="6" md="10" offset-xl="1" sm="10" xl="5">
                  <v-row class="text-uppercase font-weight-bold">
                    <v-col>{{ $t('settings.sellersDeadlines') }}</v-col>
                  </v-row>
                  <v-row class="align-center">
                    <v-col cols="8" lg="9" md="8" sm="8" xl="10">
                      <v-card-text
                        class="text-uppercase pa-0"
                      >{{ $t('settings.preparationDeadline') }}
                        <br>
                        <span :class="`v-messages theme--${isDark? 'dark' : 'light'}`">
                          {{$t('settings.maxTimePreparationMessage')}}
                        </span>
                      </v-card-text>
                    </v-col>
                    <v-col cols="4" lg="3" md="4" sm="4" xl="2">
                      <validation-provider
                        v-slot="{ errors }"
                        name="max click and collect seller delay"
                        rules="min_value:1|integer"
                      >
                        <v-text-field
                          v-model="loadedSettings.clickAndCollectSellerDelay"
                          :disabled="loading"
                          :error-messages="errors"
                          :label="$t('settings.hours')"
                          class="text-uppercase"
                          dense
                          hide-details="auto"
                          outlined
                          required
                          type="number"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                  </v-row>
                  <v-row class="align-center">
                    <v-col cols="8" lg="9" md="8" sm="8" xl="10">
                      <v-card-text
                        class="text-uppercase pa-0"
                      >{{ $t('settings.preparationSlotsSearchPeriod') }}
                        <br>
                        <span :class="`v-messages theme--${isDark? 'dark' : 'light'}`">
                          {{$t('settings.preparationSlotsSearchPeriodMessage')}}
                        </span>
                      </v-card-text>
                    </v-col>
                    <v-col cols="4" lg="3" md="4" sm="4" xl="2">
                      <validation-provider
                        v-slot="{ errors }"
                        name="C&C prepration slots seach period"
                        rules="min_value:1|integer"
                      >
                        <v-text-field
                          v-model="loadedSettings.clickAndCollectPreparationSlotsSearchPeriod"
                          :disabled="loading"
                          :error-messages="errors"
                          :label="$t('settings.days')"
                          class="text-uppercase"
                          dense
                          hide-details="auto"
                          outlined
                          required
                          type="number"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
          <v-card class="mb-5" outlined>
            <v-card-subtitle>{{ $t('subOrder.shipFromStore') }}</v-card-subtitle>
            <v-container fluid>
              <v-row>
                <v-col cols="12" lg="6" md="10" offset-xl="1" sm="10" xl="5">
                  <v-row class="text-uppercase font-weight-bold">
                    <v-col>{{ $t('settings.sellersDeadlines') }}</v-col>
                  </v-row>
                  <v-row class="align-center">
                    <v-col cols="8" lg="9" md="8" sm="8" xl="10">
                      <v-card-text
                        class="text-uppercase pa-0"
                      >{{ $t('settings.preparationDeadline') }}
                        <br>
                        <span :class="`v-messages theme--${isDark? 'dark' : 'light'}`">
                          {{$t('settings.maxTimePreparationMessage')}}
                        </span>
                      </v-card-text>
                    </v-col>
                    <v-col cols="4" lg="3" md="4" sm="4" xl="2">
                      <validation-provider
                        v-slot="{ errors }"
                        name="max ship from store seller delay"
                        rules="min_value:1|integer"
                      >
                        <v-text-field
                          v-model="loadedSettings.shipFromStoreSellerDelay"
                          :disabled="loading"
                          :error-messages="errors"
                          :label="$t('settings.hours')"
                          class="text-uppercase"
                          dense
                          hide-details="auto"
                          outlined
                          required
                          type="number"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                  </v-row>
                  <v-row class="align-center">
                    <v-col cols="8" lg="9" md="8" sm="8" xl="10">
                      <v-card-text
                        class="text-uppercase pa-0"
                      >{{ $t('settings.preparationSlotsSearchPeriod') }}
                        <br>
                        <span :class="`v-messages theme--${isDark? 'dark' : 'light'}`">
                          {{$t('settings.preparationSlotsSearchPeriodMessage')}}
                    </span>
                      </v-card-text>
                    </v-col>
                    <v-col cols="4" lg="3" md="4" sm="4" xl="2">
                      <validation-provider
                        v-slot="{ errors }"
                        name="SFS preparation slots search period"
                        rules="min_value:1|integer"
                      >
                        <v-text-field
                          v-model="loadedSettings.shipFromStorePreparationSlotsSearchPeriod"
                          :disabled="loading"
                          :error-messages="errors"
                          :label="$t('settings.days')"
                          class="text-uppercase"
                          dense
                          hide-details="auto"
                          outlined
                          required
                          type="number"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-container>
      </v-card>
      <v-card class="mb-5 mt-5" outlined>
        <v-card-title>{{ $t('subOrder.clickAndReserve') }}</v-card-title>
        <v-container fluid>
          <v-row>
            <v-col cols="12" lg="6" md="10" offset-xl="1" sm="10" xl="5">
              <v-row class="text-uppercase font-weight-bold">
                <v-col>{{ $t('settings.sellersDeadlines') }}</v-col>
              </v-row>
              <v-row class="align-center">
                <v-col cols="8" lg="9" md="8" sm="8" xl="10">
                  <v-card-text
                    class="text-uppercase pa-0"
                  >{{ $t('settings.preparationDeadline') }}
                    <br>
                    <span :class="`v-messages theme--${isDark? 'dark' : 'light'}`">
                      {{$t('settings.maxTimePreparationMessage')}}
                    </span>
                  </v-card-text>
                </v-col>
                <v-col cols="4" lg="3" md="4" sm="4" xl="2">
                  <validation-provider
                    v-slot="{ errors }"
                    name="max click and reserve seller delay"
                    rules="min_value:1|integer"
                  >
                    <v-text-field
                      v-model="loadedSettings.eReservationSellerDelay"
                      :disabled="loading"
                      :error-messages="errors"
                      :label="$t('settings.hours')"
                      class="text-uppercase"
                      dense
                      hide-details="auto"
                      outlined
                      required
                      type="number"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <v-row class="align-center">
                <v-col cols="8" lg="9" md="8" sm="8" xl="10">
                  <v-card-text
                    class="text-uppercase pa-0"
                  >{{ $t('settings.preparationSlotsSearchPeriod') }}
                    <br>
                    <span :class="`v-messages theme--${isDark? 'dark' : 'light'}`">
                          {{$t('settings.preparationSlotsSearchPeriodMessage')}}
                    </span>
                  </v-card-text>
                </v-col>
                <v-col cols="4" lg="3" md="4" sm="4" xl="2">
                  <validation-provider
                    v-slot="{ errors }"
                    name="eresa preparation slots search period"
                    rules="min_value:1|integer"
                  >
                    <v-text-field
                      v-model="loadedSettings.eReservationPreparationSlotsSearchPeriod"
                      :disabled="loading"
                      :error-messages="errors"
                      :label="$t('settings.days')"
                      class="text-uppercase"
                      dense
                      hide-details="auto"
                      outlined
                      required
                      type="number"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" lg="6" md="10" sm="10" xl="5">
              <v-row class="text-uppercase font-weight-bold">
                <v-col>{{ $t('settings.clientsDeadlines') }}</v-col>
              </v-row>
              <v-row class="align-center">
                <v-col cols="8" lg="9" md="8" sm="8" xl="10">
                  <v-card-text
                    class="text-uppercase pa-0"
                  >{{ $t('settings.clientPickupDeadline') }}
                  </v-card-text>
                </v-col>
                <v-col cols="4" lg="3" md="4" sm="4" xl="2">
                  <validation-provider
                    v-slot="{ errors }"
                    name="max click and reserve client delay"
                    rules="min_value:1|integer"
                  >
                    <v-text-field
                      v-model="loadedSettings.eReservationClientDelay"
                      :disabled="loading"
                      :error-messages="errors"
                      :label="$t('settings.days')"
                      class="text-uppercase"
                      dense
                      hide-details="auto"
                      outlined
                      required
                      type="number"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-switch
                    v-model="loadedSettings.activateClickAndReservePickupTimeout"
                    :label="$t('settings.activateClickAndReservePickupTimeout')"
                    :messages="$t('settings.activateClickAndReservePickupTimeoutMessage')"
                  />
                  <v-row v-show="loadedSettings.activateClickAndReservePickupTimeout" class="pl-10">
                    <v-col cols="12">
                      <v-switch
                        v-model="loadedSettings.activateClickAndReservePickupDeadlineAdjustment"
                        :label="$t('settings.activateClickAndReservePickupDeadlineAdjustment')"
                        :messages="$t('settings.activateClickAndReservePickupDeadlineAdjustmentMessage')"
                        dense
                      />
                    </v-col>
                    <v-col v-if="loadedSettings.activateClickAndReservePickupDeadlineAdjustment" cols="6" lg="5">
                      <validation-provider v-slot="{ errors }" name="c&c pickup timeout adjustment hour" rules="required">
                        <v-text-field
                          v-model="loadedSettings.clickAndReservePickupDeadlineAdjustmentHour"
                          :error-messages="errors"
                          :label="$t('settings.preciseHour')"
                          dense
                          type="time">
                        </v-text-field>
                      </validation-provider>
                    </v-col>
                    <v-col cols="12">
                      <v-switch
                        v-model="loadedSettings.activateClickAndReservePickupDeadlineExtension"
                        :label="$t('settings.activatePickupDeadlineExtension')"
                        :messages="$t('settings.activatePickupDeadlineExtensionMessage')"
                        dense
                      />
                    </v-col>
                    <v-col v-if="loadedSettings.activateClickAndReservePickupDeadlineExtension" offset="1"  cols="7" lg="8" md="7" sm="7" xl="9">
                      <v-card-text class="text-uppercase pa-0">
                        {{ $t('settings.pickupDeadlineExtensionLimitMessage') }}
                      </v-card-text>
                    </v-col>
                    <v-col v-if="loadedSettings.activateClickAndReservePickupDeadlineExtension" cols="4" lg="3" md="4" sm="4" xl="2">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('settings.clickAndReservePickupDeadlineExtensionLimit')"
                        rules="required|min_value:1"
                      >
                        <v-text-field
                          v-model="loadedSettings.clickAndReservePickupDeadlineExtensionLimit"
                          :error-messages="errors"
                          :label="$t('settings.days')"
                          dense
                          outlined
                          type="number"
                          hide-details="auto"
                        >
                        </v-text-field>
                      </validation-provider>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-form>
  </v-container>
</template>

<script>
import { ValidationProvider, extend } from 'vee-validate'
import { min_value, numeric } from 'vee-validate/dist/rules'

extend('min_value', {
  ...min_value,
  message: (fieldName, placeholders) => {
    var minute = placeholders.unit == undefined ? '' : placeholders.unit
    return `${fieldName} is < ${placeholders.min} ` + minute
  },
  params: ['min', 'unit']
})
extend('numeric', { ...numeric })

export default {
  name: 'DelaysSettings',
  components: {
    'validation-provider': ValidationProvider,
  },
  props: {
    loadedSettings: {
      type: Object,
      required: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    isDark: {
      type: Boolean
    }
  },
  data () {
    return {
      countries: [], // contains all selectedCountry
      selectedCountry: '', // country code of actual country
      isCountryCodeExists: false
    }
  }
}
</script>

<style>

</style>